import { useState } from 'react'
import { FileType, Share2, Check, Activity, Shield, AlertTriangle, Brain, Clock } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import ScoreDisplay from './ScoreDisplay'
import IOCCard from './IOCCard'
import CollapsibleSection from './CollapsibleSection'
import type { AnalysisResult } from '../types'

interface AnalysisResultsProps {
  result: AnalysisResult
}

function formatLogName(logName: string, logFormat: string): string {
  if (logFormat === 'gcp_audit' && logName.includes('.')) {
    // Format GCP method names like "storage.buckets.create" -> "Storage Buckets Create"
    const parts = logName.split('.')
    if (parts.length >= 3) {
      const service = parts[0] || parts[1] // handle both "storage.buckets.create" and "v1.storage.buckets.create"
      const resource = parts[1] || parts[2]
      const action = parts[2] || parts[3]
      
      if (service && resource && action) {
        const serviceName = service.charAt(0).toUpperCase() + service.slice(1)
        const resourceName = resource.charAt(0).toUpperCase() + resource.slice(1)
        const actionName = action.charAt(0).toUpperCase() + action.slice(1)
        return `${serviceName} ${resourceName} ${actionName}`
      }
    }
  }
  
  return logName
}

interface MitreTactic {
  id: string
  name: string
  techniques: MitreTechnique[]
}

interface MitreTechnique {
  id: string
  name: string
  subTechniques: MitreSubTechnique[]
}

interface MitreSubTechnique {
  id: string
  name: string
}

function parseMitreAttackMappings(mappings: string[]): MitreTactic[] {
  const tacticsMap = new Map<string, MitreTactic>()
  
  mappings.forEach(mapping => {
    // Parse mappings like "TA0003 - Persistence", "T1098 - Account Manipulation", etc.
    const tacticMatch = mapping.match(/^(TA\d+)\s*-\s*(.+)$/)
    const techniqueMatch = mapping.match(/^(T\d+(?:\.\d+)?)\s*-\s*(.+)$/)
    
    if (tacticMatch) {
      const [, tacticId, tacticName] = tacticMatch
      if (!tacticsMap.has(tacticId)) {
        tacticsMap.set(tacticId, {
          id: tacticId,
          name: tacticName,
          techniques: []
        })
      }
    } else if (techniqueMatch) {
      const [, techniqueId, techniqueName] = techniqueMatch
      
      // Check if this is a sub-technique (contains a dot)
      const isSubTechnique = techniqueId.includes('.')
      let parentTechniqueId = techniqueId
      
      if (isSubTechnique) {
        parentTechniqueId = techniqueId.split('.')[0] // T1098.003 -> T1098
      }
      
      // Try to infer which tactic this technique belongs to
      let tacticId = inferTacticFromTechnique(parentTechniqueId, techniqueName)
      
      // If we can't infer, create a general techniques category
      if (!tacticId) {
        tacticId = 'TECHNIQUES'
        if (!tacticsMap.has(tacticId)) {
          tacticsMap.set(tacticId, {
            id: tacticId,
            name: 'Additional Techniques',
            techniques: []
          })
        }
      }
      
      // Ensure the tactic exists
      if (!tacticsMap.has(tacticId)) {
        tacticsMap.set(tacticId, {
          id: tacticId,
          name: getTacticName(tacticId),
          techniques: []
        })
      }
      
      const tactic = tacticsMap.get(tacticId)!
      
      if (isSubTechnique) {
        // Find or create the parent technique
        let parentTechnique = tactic.techniques.find(t => t.id === parentTechniqueId)
        if (!parentTechnique) {
          // Create parent technique if it doesn't exist
          parentTechnique = {
            id: parentTechniqueId,
            name: getParentTechniqueName(parentTechniqueId),
            subTechniques: []
          }
          tactic.techniques.push(parentTechnique)
        }
        
        // Add sub-technique
        parentTechnique.subTechniques.push({
          id: techniqueId,
          name: techniqueName
        })
      } else {
        // Regular technique - check if it already exists
        let existingTechnique = tactic.techniques.find(t => t.id === techniqueId)
        if (!existingTechnique) {
          tactic.techniques.push({
            id: techniqueId,
            name: techniqueName,
            subTechniques: []
          })
        }
      }
    }
  })
  
  // Sort techniques and sub-techniques
  tacticsMap.forEach(tactic => {
    tactic.techniques.sort((a, b) => a.id.localeCompare(b.id))
    tactic.techniques.forEach(technique => {
      technique.subTechniques.sort((a, b) => a.id.localeCompare(b.id))
    })
  })
  
  return Array.from(tacticsMap.values()).sort((a, b) => {
    // Sort tactics by ID, with "TECHNIQUES" at the end
    if (a.id === 'TECHNIQUES') return 1
    if (b.id === 'TECHNIQUES') return -1
    return a.id.localeCompare(b.id)
  })
}

function getParentTechniqueName(techniqueId: string): string {
  const parentTechniqueNames: Record<string, string> = {
    'T1098': 'Account Manipulation',
    'T1021': 'Remote Services',
    'T1548': 'Abuse Elevation Control Mechanism',
    'T1078': 'Valid Accounts',
    'T1136': 'Create Account',
    'T1546': 'Event Triggered Execution',
    'T1562': 'Impair Defenses',
    'T1578': 'Modify Cloud Compute Infrastructure',
    'T1535': 'Unused/Unsupported Cloud Regions',
    'T1580': 'Cloud Infrastructure Discovery',
    'T1087': 'Account Discovery',
    'T1530': 'Data from Cloud Storage',
    'T1537': 'Transfer Data to Cloud Account'
  }
  
  return parentTechniqueNames[techniqueId] || techniqueId
}

function inferTacticFromTechnique(techniqueId: string, _techniqueName: string): string | null {
  // Common technique to tactic mappings based on MITRE ATT&CK framework
  const techniqueToTactic: Record<string, string> = {
    // Persistence (TA0003)
    'T1098': 'TA0003', // Account Manipulation -> Persistence
    'T1098.003': 'TA0003', // Additional Cloud Roles -> Persistence
    'T1136': 'TA0003', // Create Account -> Persistence
    'T1078': 'TA0003', // Valid Accounts -> Persistence
    
    // Privilege Escalation (TA0004)  
    'T1548': 'TA0004', // Abuse Elevation Control Mechanism -> Privilege Escalation
    'T1546': 'TA0004', // Event Triggered Execution -> Privilege Escalation
    
    // Defense Evasion (TA0005)
    'T1562': 'TA0005', // Impair Defenses -> Defense Evasion
    'T1578': 'TA0005', // Modify Cloud Compute Infrastructure -> Defense Evasion
    'T1535': 'TA0005', // Unused/Unsupported Cloud Regions -> Defense Evasion
    
    // Lateral Movement (TA0008)
    'T1021': 'TA0008', // Remote Services -> Lateral Movement
    'T1021.007': 'TA0008', // Cloud Services -> Lateral Movement
    'T1021.008': 'TA0008', // Direct Cloud VM Connections -> Lateral Movement
    
    // Discovery (TA0007)
    'T1580': 'TA0007', // Cloud Infrastructure Discovery -> Discovery
    'T1087': 'TA0007', // Account Discovery -> Discovery
    
    // Collection (TA0009)
    'T1530': 'TA0009', // Data from Cloud Storage -> Collection
    
    // Exfiltration (TA0010)
    'T1537': 'TA0010', // Transfer Data to Cloud Account -> Exfiltration
  }
  
  return techniqueToTactic[techniqueId] || null
}

function getTacticName(tacticId: string): string {
  const tacticNames: Record<string, string> = {
    'TA0001': 'Initial Access',
    'TA0002': 'Execution', 
    'TA0003': 'Persistence',
    'TA0004': 'Privilege Escalation',
    'TA0005': 'Defense Evasion',
    'TA0006': 'Credential Access',
    'TA0007': 'Discovery',
    'TA0008': 'Lateral Movement',
    'TA0009': 'Collection',
    'TA0010': 'Exfiltration',
    'TA0040': 'Impact'
  }
  
  return tacticNames[tacticId] || tacticId
}

export default function AnalysisResults({ result }: AnalysisResultsProps) {
  const [copied, setCopied] = useState(false)

  const handleShare = async () => {
    const mitreText = result.mitre_attack.length > 0 
      ? `\n🎯 MITRE ATT&CK: ${result.mitre_attack.join(', ')}\n`
      : ''
    
    const shareText = `🛡️ Cloudots Security Analysis

📊 Security Score: ${result.overall_score.score}/100 (${result.overall_score.level})
🔍 Threats Detected: ${result.threats_detected}
🚨 IOC Matches: ${result.ioc_matches?.length || 0}
📁 Log Type: ${result.log_format.replace('_', ' ').toUpperCase()} (${formatLogName(result.log_name, result.log_format)})${mitreText}
Analysis ID: ${result.analysis_id}
Generated: ${new Date(result.timestamp).toLocaleString()}

Powered by Cloudots - Advanced Cloud Telemetry Analysis`

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Cloudots Security Analysis',
          text: shareText
        })
      } else {
        await navigator.clipboard.writeText(shareText)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (error) {
      console.error('Share failed:', error)
    }
  }

  return (
    <div className="space-y-6">
      <CollapsibleSection 
        title="Analysis Results"
        defaultExpanded={true}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              onClick={handleShare}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200 w-full sm:w-auto justify-center sm:justify-start"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4" />
                  <span>Share</span>
                </>
              )}
            </button>
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
              ID: {result.analysis_id}
            </div>
          </div>
        </div>

        <div className={`grid grid-cols-1 ${result.event_time ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4 mb-6`}>

          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <FileType className="h-5 w-5 text-gray-600 dark:text-gray-400 mx-auto mb-1" />
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {result.log_format.replace('_', ' ').toUpperCase()}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Log Type</div>
          </div>

          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <Activity className="h-5 w-5 text-gray-600 dark:text-gray-400 mx-auto mb-1" />
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {formatLogName(result.log_name, result.log_format)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Log Name</div>
          </div>

          {result.event_time && (
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400 mx-auto mb-1" />
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {new Date(result.event_time).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Event Time</div>
            </div>
          )}
          
        </div>

        <ScoreDisplay result={result} />
      </CollapsibleSection>

      {result.ioc_matches && result.ioc_matches.length > 0 && (
        <CollapsibleSection 
          title={`Malicious Indicators (IOCs) Detected (${result.ioc_matches.length})`}
          icon={<AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />}
          defaultExpanded={false}
        >
          <div className="space-y-4">
            {result.ioc_matches.map((ioc, index) => (
              <IOCCard key={index} ioc={ioc} />
            ))}
          </div>
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            IOC data provided by ThreatFox (abuse.ch) - updated hourly
          </div>
        </CollapsibleSection>
      )}

      {(result.threats.length > 0 || result.mitre_attack.length > 0) && (
        <CollapsibleSection 
          title="Cloudots Analysis"
          icon={<Brain className="h-8 w-8 text-orange-600 dark:text-orange-400" />}
          defaultExpanded={false}
        >
          <div className="mb-6">
          
          {/* Security Implications */}
          {result.threats.length > 0 && (
            <CollapsibleSection 
              title="Security Implications"
              icon={<Shield className="h-5 w-5 text-orange-600 dark:text-orange-400" />}
              defaultExpanded={false}
              className="mb-6"
            >
              <div className="space-y-4">
                {result.threats.map((threat, index) => (
                  <div key={index} className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-base text-gray-900 dark:text-gray-100">
                        {threat.description}
                      </p>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(() => {
                        switch (threat.severity) {
                          case 'CRITICAL':
                            return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                          case 'HIGH':
                            return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300'
                          case 'MEDIUM':
                            return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300'
                          case 'LOW':
                            return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300'
                          default:
                            return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }
                      })()}`}>
                        {threat.severity}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Log Explanation */}
          {result.threats.length > 0 && result.threats.some(threat => threat.type === 'cloudots_security_event' && threat.cloudotsDescription) && (
            <CollapsibleSection 
              title="Log Explanation"
              icon={<FileType className="h-5 w-5 text-orange-400" />}
              defaultExpanded={false}
              className="mb-6"
            >
              <div>
                {result.threats.map((threat, index) => (
                  threat.type === 'cloudots_security_event' && threat.cloudotsDescription && (
                    <div key={index} className="text-base text-gray-900 dark:text-gray-100">
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a 
                              {...props} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline hover:no-underline transition-colors duration-200"
                            />
                          )
                        }}
                      >
                        {threat.cloudotsDescription}
                      </ReactMarkdown>
                    </div>
                  )
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* MITRE ATT&CK Mappings */}
          {result.mitre_attack.length > 0 && (
            <CollapsibleSection 
              title="MITRE ATT&CK Mappings"
              icon={<Shield className="h-5 w-5 text-orange-600 dark:text-orange-400" />}
              defaultExpanded={false}
              className="mt-6"
            >
              
              {(() => {
                const structuredMappings = parseMitreAttackMappings(result.mitre_attack)
                
                return (
                  <div className="space-y-4">
                    {structuredMappings.map((tactic) => (
                      <div key={tactic.id} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-800">
                        {/* Tactic Header */}
                        <div className="bg-orange-100 dark:bg-orange-900/20 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-orange-800 dark:text-orange-200">
                              <a 
                                href={`https://attack.mitre.org/tactics/${tactic.id}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-orange-600 dark:hover:text-orange-300 underline hover:no-underline transition-colors duration-200"
                              >
                                {tactic.id} - {tactic.name}
                              </a>
                            </div>
                          </div>
                        </div>
                        
                        {/* Techniques List */}
                        {tactic.techniques.length > 0 && (
                          <div className="p-4">
                            <div className="space-y-3">
                              {tactic.techniques.map((technique) => (
                                <div key={technique.id} className="space-y-2">
                                  {/* Main Technique */}
                                  <a 
                                    href={`https://attack.mitre.org/techniques/${technique.id}/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center p-3 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors duration-200 underline hover:no-underline"
                                  >
                                    <div className="flex-shrink-0 w-16 text-xs font-mono text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded text-center">
                                      {technique.id}
                                    </div>
                                    <div className="ml-3 text-sm text-gray-900 dark:text-gray-100">
                                      {technique.name}
                                    </div>
                                  </a>
                                  
                                  {/* Sub-Techniques */}
                                  {technique.subTechniques.length > 0 && (
                                    <div className="ml-8 space-y-2">
                                      {technique.subTechniques.map((subTechnique) => (
                                        <a 
                                          key={subTechnique.id}
                                          href={`https://attack.mitre.org/techniques/${subTechnique.id.replace('.', '/')}/`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center p-3 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 hover:from-slate-100 hover:to-slate-200 dark:hover:from-slate-600 dark:hover:to-slate-700 transition-colors duration-200 underline hover:no-underline"
                                        >
                                          <div className="flex-shrink-0 w-18 text-xs font-mono text-slate-600 dark:text-slate-400 bg-slate-200 dark:bg-slate-600 px-2 py-1 rounded">
                                            {subTechnique.id}
                                          </div>
                                          <div className="ml-3 text-sm text-slate-800 dark:text-slate-200">
                                            {subTechnique.name}
                                          </div>
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              })()}
            </CollapsibleSection>
          )}
        </div>
        </CollapsibleSection>
      )}
    </div>
  )
}